/* ──────────────────────────────────────────────────────────────────────
 *  Auth Client — Team D owns this file
 *  Wraps @pulse-business-ai/auth-client for portal use.
 *  In mock mode, provides a fake session.
 * ────────────────────────────────────────────────────────────────────── */

import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { env, isMockMode } from "@/lib/env";

// ─── Types ───────────────────────────────────────────────────────────

export interface PulseUser {
  id: string;
  email: string;
  displayName: string | null;
  avatarUrl: string | null;
}

export interface AuthState {
  user: PulseUser | null;
  accessToken: string | null;
  loading: boolean;
  signIn: () => void;
  signOut: () => void;
}

interface StoredAuthSession {
  user: PulseUser;
  accessToken: string;
  expiresAt: number;
}

// ─── Mock User ───────────────────────────────────────────────────────

const MOCK_USER: PulseUser = {
  id: "mock-user-1",
  email: "demo@pulsebusiness.ai",
  displayName: "Demo User",
  avatarUrl: null,
};

// ─── Context ─────────────────────────────────────────────────────────

const AuthContext = createContext<AuthState>({
  user: null,
  accessToken: null,
  loading: true,
  signIn: () => {},
  signOut: () => {},
});

export function useAuth(): AuthState {
  return useContext(AuthContext);
}

// ─── Provider ────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PulseUser | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isMockMode()) {
      // Mock mode: auto-sign-in
      setUser(MOCK_USER);
      setAccessToken("mock-access-token");
      setLoading(false);
      return;
    }

    // Real auth: check for Supabase token in hash or existing session
    initRealAuth().then((session) => {
      setUser(session?.user ?? null);
      setAccessToken(session?.accessToken ?? null);
      setLoading(false);
    });
  }, []);

  const signIn = () => {
    if (isMockMode()) {
      setUser(MOCK_USER);
      return;
    }
    // Redirect to Pulse Auth with return URL
    const returnUrl = encodeURIComponent(window.location.href);
    window.location.href = `${env.authLoginUrl}/login?redirect_to=${returnUrl}`;
  };

  const signOut = () => {
    setUser(null);
    setAccessToken(null);
    if (!isMockMode()) {
      localStorage.removeItem("pulse-auth-session");
      // Clear Supabase session and redirect
      window.location.href = `${env.authLoginUrl}/logout`;
    }
  };

  return (
    <AuthContext.Provider value={{ user, accessToken, loading, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

// ─── Real Auth Bootstrap ─────────────────────────────────────────────

async function initRealAuth(): Promise<StoredAuthSession | null> {
  try {
    // Check for hash-based token (Supabase redirect)
    const hash = window.location.hash;
    if (hash.includes("access_token=")) {
      const params = new URLSearchParams(hash.substring(1));
      const accessToken = params.get("access_token");
      if (accessToken) {
        // Clear hash from URL
        window.history.replaceState(null, "", window.location.pathname + window.location.search);

        // Decode JWT to get user info (basic extraction)
        const payload = JSON.parse(atob(accessToken.split(".")[1]!));
        const session = {
          user: {
            id: payload.sub,
            email: payload.email,
            displayName: payload.user_metadata?.full_name || payload.email,
            avatarUrl: payload.user_metadata?.avatar_url || null,
          },
          accessToken,
          expiresAt: Number(payload.exp ?? 0) > 0 ? Number(payload.exp) * 1000 : Date.now() + 3600_000,
        };
        localStorage.setItem("pulse-auth-session", JSON.stringify(session));
        return session;
      }
    }

    // Check for existing session in localStorage
    const stored = localStorage.getItem("pulse-auth-session");
    if (stored) {
      const session = JSON.parse(stored) as StoredAuthSession;
      if (session.user && session.accessToken && session.expiresAt > Date.now()) {
        return session;
      }
      localStorage.removeItem("pulse-auth-session");
    }

    return null;
  } catch {
    return null;
  }
}
